import {BannerInfo, InfiniteScroll} from '@jobseeker/lotus/components';
import {Box, Grid} from '@jobseeker/lotus/foundations';
import {Button, Link, Skeleton, Typography} from '@mui/material';
import {HiristCalendar, BannerIcon} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {useEffect, useState} from 'react';
import {Select, Tooltip} from '@jobseeker/lotus/elements';
import {interviewService} from '@jobseeker/common-core/services';
import dayjs from 'dayjs';
import Loader from '@jobseeker/lotus/components/Loader';
import {
  decodeHexBase64,
  openUrl,
  showErrorToast,
} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {useSearchParams} from 'next/navigation';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import {enqueueSnackbar} from 'notistack';
import {VersionVariants} from '@jobseeker/lotus/enums';
import JobTitle from './JobTitle';
import InterviewType from './InterviewType';
import HiristBannerComponent from '@jobseeker/lotus/components/HiristBanner';
import NoInterviews from './NoInterviews';

interface InterviewSlotProps {
  cancelLoading?: boolean;
  booked?: boolean;
  disabled?: boolean;
  inviteStatus?: number;
  date?: string;
  timing?: string;
  calendarId?: string;
  slotId?: string;
  slotType?: string;
  recruiterId?: number;
  isAboveLarge?: boolean;
  handleCancelSlot: (payload: {
    slotId: string;
    calendarId: string;
    recruiterId: number;
  }) => void;
}
const InterviewSlot = ({
  cancelLoading = false,
  booked = false,
  disabled = false,
  date = '',
  timing = '',
  calendarId = '0',
  slotId = '0',
  recruiterId = 0,
  isAboveLarge = true,
  slotType = '',
  handleCancelSlot,
}: InterviewSlotProps) => {
  if (booked && !disabled && (!timing || !date)) {
    return <></>;
  }
  const theme = useWrappedTheme();
  const militaryTime = dayjs(timing, 'h:mm A').format('HH:mm');
  const currentMilitaryTime = dayjs().format('HH:mm');

  if (booked && !disabled && date !== 'Invalid date') {
    return (
      <>
        <Box>
          <Box
            width={{xs: '100%', lg: '150px'}}
            height={'46px'}
            p={0}
            data-testid="interview_slot"
            display="flex"
            justifyContent={isAboveLarge ? 'left' : 'right'}
            gap={1}
          >
            <Image src={HiristCalendar} alt="calendar" height={20} width={20} />
            <Box textAlign="start">
              <Typography fontSize={14} fontWeight={400} textAlign="center">
                {dayjs(date).format('DD-MM-YYYY')}
              </Typography>
              <Typography fontSize={14} fontWeight={400}>
                {timing}
              </Typography>
            </Box>
          </Box>
          {isAboveLarge &&
            slotType === 'VIDEO' &&
            dayjs(date).isSame(dayjs(), 'day') && (
              <Button
                data-testid="join_room_desktop"
                onClick={() =>
                  openUrl(
                    `/interview/meet?inviteId=${decodeHexBase64(
                      slotId
                    )}&calleeId=${recruiterId}&ref=interviews`,
                    '_blank'
                  )
                }
                variant="outlined"
                sx={{
                  width: {xxs: 142, md: 120},
                  height: '32px',
                  borderRadius: 1,
                  fontSize: 14,
                  color: 'white !important',
                  backgroundColor: `${theme.palette.primary.main} !important`,
                  ':hover': {
                    borderColor: theme.palette.primary.main,
                    '.searchbar-icon': {
                      filter: 'brightness(10)',
                    },
                  },
                  mb: 0.5,
                }}
              >
                Join room
              </Button>
            )}
          <Box
            display="flex"
            justifyContent={'space-between'}
            color={theme.palette.primary.main}
            maxWidth={170}
            mt={1.5}
          >
            {(dayjs(date).isAfter(dayjs(), 'day') ||
              (dayjs(date).isSame(dayjs(), 'day') &&
                currentMilitaryTime < militaryTime)) && (
              <>
                <Link
                  href={`/cal/pick-date/${calendarId}/${slotId}?q=rc&ref=myInterviews&reschedule=1`}
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 400,
                    pr: 1.5,
                    height: 15,
                    lineHeight: 1,
                    borderRight: `1px solid ${theme.palette.primary.main}`,
                  }}
                >
                  Reschedule
                </Link>
                <Button
                  onClick={() => {
                    handleCancelSlot({slotId, calendarId, recruiterId});
                  }}
                  variant="text"
                  sx={{
                    p: 0,
                    fontSize: 14,
                    color: theme.palette.primary.main,
                    fontWeight: 400,
                    height: 15,
                    position: 'relative',
                    left: {xs: 0, md: -18},
                    ':hover': {
                      bgcolor: 'transparent',
                    },
                  }}
                  disabled={cancelLoading}
                  data-testid="cancel_slot_desktop"
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
      </>
    );
  } else if (disabled) {
    return (
      <Tooltip
        placement="bottom-start"
        arrow
        title={
          <Typography
            variant="subtitle3"
            lineHeight="16px"
            sx={{
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
            }}
          >
            All Interview Slots have been booked by other Candidates. We will
            come back to you as and when more interview slots open up.
          </Typography>
        }
        sx={{
          maxWidth: isAboveLarge ? '430px' : '300px',
          '.MuiTooltip-arrow': {
            left: '-30px !important',
          },
        }}
      >
        <Button
          data-testid="choose_slot_disabled"
          variant="outlined"
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.16)',
            width: {xxs: 142, md: 120},
            height: 32,
            borderRadius: 1,
            border: 'none !important',
            fontSize: 14,
            color: 'rgba(0, 0, 0, 0.26)',
            cursor: 'initial',
            ':hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.16)',
            },
          }}
          disableRipple
        >
          Choose Slot
        </Button>
      </Tooltip>
    );
  }
  return (
    <Button
      data-testid="choose_slot"
      variant="outlined"
      sx={{
        width: {xxs: 142, md: 120},
        height: 32,
        borderRadius: 1,
        fontSize: 14,
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        ':hover': {
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          borderColor: theme.palette.primary.main,
        },
      }}
      onClick={() => {
        openUrl(
          `/cal/pick-date/${calendarId}/${slotId}?q=rc&ref=myInterviews`,
          '_blank'
        );
      }}
    >
      Choose Slot
    </Button>
  );
};

const Interviews = () => {
  const router = useRouter();
  const theme = useWrappedTheme();
  const searchParams = useSearchParams();
  const bookedParam: 0 | 1 | 2 = parseInt(
    searchParams?.get('booked') ?? '2',
    10
  ) as 0 | 1 | 2;

  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<0 | 1 | 2>(bookedParam);
  const filterValue = ['Non-booked Slots', 'Booked Slots', 'All Slots'];
  const [booked, setBooked] = useState<0 | 1 | 2>(bookedParam);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [cancel, setCancel] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [slots, setSlots] = useState<
    {
      slotId: string;
      calendarId: string;
      address: string;
      messageTelephonic: string;
      slotType: string;
      inviteStatus: number;
      recruiterId: number;
      slotDate: string;
      slotTime: string;
      status: number;
      remainingSlotsCount: number;
      canceledBy: number;
      jobInfo: {
        id: number;
        title: string;
        minExp: number;
        maxExp: number;
        locations: string;
        confidential: number;
      };
      recruiterInfo: {
        name: string;
        organization: string;
        designation: string;
        profileLink: string;
        imgUrl: string;
      };
    }[]
  >([]);
  const [menuOpen, setOpen] = useState<string | boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setMenuOpen = (value: any) => {
    if (value && value === menuOpen) {
      setMenuOpen(false);
    } else setOpen(value);
  };

  const isAboveLarge = useIsLargeDevice(900);
  const isLargeDevice = useIsLargeDevice('md');
  const pageContent = 10;

  useEffect(() => {
    (async () => {
      const data = await interviewService.getSlots(
        booked,
        pageNumber,
        pageContent
      );

      if (!data || !data?.data) {
        return;
      }

      setHasMore(data?.data?.length === pageContent);
      setIsLoading(false);
      setSlots(prevData => [...prevData, ...data.data]);
    })();
  }, [booked, cancel, pageNumber]);

  const ContainerComponent = InfiniteScroll;

  const handleCancelSlot = async ({
    slotId,
    calendarId,
    recruiterId,
  }: {
    slotId: string;
    calendarId: string;
    recruiterId: number;
  }) => {
    try {
      setCancelLoading(true);
      const response = await interviewService.cancelInterview({
        slotId,
        calendarId,
        recruiterId,
      });

      if (response?.error) {
        throw new Error(response.error.message);
      }

      setCancel(true); //resetting to default state
      setPageNumber(1);
      setIsLoading(true);
      setSlots([]);
      enqueueSnackbar('Your Interview Slot Details has been cancelled', {
        variant: 'success',
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (String(error?.message ?? '').includes('Lot Id not found')) {
        // Scenario: The user has the /interviews page open in multiple tabs.
        // If the user cancels a slot in one tab and then switches to another tab,
        // they may attempt to cancel the same slot again.
        // Since the slot is already canceled, a toast message "Lot Id not found" will be displayed.
        // In this case, show appropriate message and ensure the latest data is reflected.
        showErrorToast('The slot has already been canceled.', error);
        setCancel(true); //resetting to default state
        setPageNumber(1);
        setIsLoading(true);
        setSlots([]);
        setCancelLoading(false);
        return;
      }
      showErrorToast(
        error.message || 'Failed to cancel the slot. Please try again.',
        error
      );
    } finally {
      setCancelLoading(false);
    }
  };

  const submitFilter = () => {
    if (filter !== booked) {
      setBooked(filter);
      setPageNumber(1);
      setIsLoading(true);
      setSlots([]);
      router.push({
        pathname: router.pathname,
        query: {
          ...router.query,
          booked: filter,
        },
      });
    }
  };

  return (
    <Box mt={{xxs: -1, md: 0}}>
      <HiristBannerComponent>
        <BannerInfo
          Icon={BannerIcon}
          isMobile={!isLargeDevice}
          title={'My Interviews'}
          subTitle={`Check here all your interview invite(s), confirmed slots details
              etc.`}
        />
      </HiristBannerComponent>
      <Box
        sx={{
          backgroundColor: 'transparent',
          px: {xxs: 1, md: 6},
          pt: {xxs: 1.5, md: 3},
          pb: {xxs: 2, md: 3},
          ml: 'auto',
          mr: 'auto',
        }}
      >
        <Grid
          container
          justifyContent={'space-between'}
          mb={0}
          pl={{xxs: 1, md: 0}}
        >
          <Grid item alignSelf={'center'}>
            <Typography
              fontWeight={isAboveLarge ? 700 : 400}
              fontSize={isAboveLarge ? 20 : 16}
              lineHeight={{xxs: '20px', md: '30px'}}
              mb={0}
              ml={{xxs: 1, md: 0}}
            >
              My Interviews
            </Typography>
          </Grid>
          <Grid item>
            <Grid container gap={1}>
              {isAboveLarge && (
                <Grid item alignSelf="center">
                  <Typography>Filter By</Typography>
                </Grid>
              )}
              <Grid item>
                <Select
                  name="slots-filter"
                  menuOpen={menuOpen === 'slots-filter'}
                  label={filterValue[filter]}
                  variant={VersionVariants.VERSION_TWO}
                  setMenuOpen={setMenuOpen}
                  onChange={event => {
                    if (isAboveLarge) {
                      setFilter(event.target.value as 0 | 1 | 2);
                    } else {
                      setFilter(() => {
                        const newFilterValue = event.target.value as 0 | 1 | 2;

                        if (newFilterValue !== booked) {
                          setBooked(newFilterValue);
                          setPageNumber(1);
                          setIsLoading(true);
                          setSlots([]);
                          router.push({
                            pathname: router.pathname,
                            query: {
                              ...router.query,
                              booked: newFilterValue,
                            },
                          });
                        }

                        return newFilterValue;
                      });
                    }
                  }}
                  options={[
                    {
                      label: 'All Slots',
                      value: 2,
                    },
                    {
                      label: 'Booked Slots',
                      value: 1,
                    },
                    {
                      label: 'Non-booked Slots',
                      value: 0,
                    },
                  ]}
                  sx={{
                    width: isAboveLarge ? 192 : 'fit-content',
                    maxWidth: 192,
                    height: isAboveLarge ? 37 : 'fit-content',
                    borderRadius: '32px',
                    fontSize: 14,
                    fontWeight: '500',
                    p: 0,
                    '& .MuiMenuItem-root>.MuiTypography-root': {
                      fontSize: '16px !important',
                    },
                    '& .MuiInputBase-input': {
                      width: isAboveLarge ? 192 : 'fit-content',
                      pr: '40px !important',
                      opacity: 0.5,
                      ...(!isAboveLarge && {
                        py: 0,
                        bgcolor: 'transparent !important',
                        border: 'none !important',
                      }),
                    },
                    ...(!isAboveLarge && {
                      '& .MuiSelect-icon': {
                        opacity: 0.5,
                      },
                    }),
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          '& p': {
                            fontSize: '16px !important',
                          },
                          '&:hover': {
                            backgroundColor: 'rgb(239, 243, 250)',
                            color: 'white',
                          },
                        },
                      },
                    },
                  }}
                />
              </Grid>
              {isAboveLarge && (
                <Grid item>
                  <Button
                    type="submit"
                    variant="outlined"
                    sx={{
                      zIndex: 2,
                      width: '100%',
                      fontSize: {xxs: 12, md: 16},
                      lineHeight: '18px',
                      borderRadius: 6,
                      px: {xxs: 1, md: 2.5},
                      py: {xxs: 1, md: 1},
                      pl: {xxs: 1.5, md: 2.5},
                      '.MuiButton-startIcon': {
                        mr: {xxs: 0.5, md: 0.75},
                      },
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      ':hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                    onClick={submitFilter}
                  >
                    Apply
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>

        {isLoading ? (
          <Box px={1} pb={4}>
            <Skeleton
              variant="rounded"
              width={'50%'}
              height={21}
              sx={{
                mt: 3,
                mb: 1,
              }}
            />
            {[
              ...new Array(5).fill('').map(index => (
                <>
                  <Box display="flex" justifyContent="space-between" mt={2}>
                    {[...Array(4)].map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="rounded"
                        width={'22%'}
                        height={14}
                      />
                    ))}
                  </Box>
                  <Skeleton
                    variant="rounded"
                    width={'50%'}
                    height={14}
                    sx={{mt: 1}}
                  />
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    sx={{my: 1}}
                  >
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      sx={{mr: 2}}
                    />

                    <Box flexGrow={1}>
                      <Skeleton
                        variant="rounded"
                        width={'70%'}
                        height={16}
                        sx={{mb: 0.5}}
                      />
                      <Skeleton variant="rounded" width={'50%'} height={14} />
                    </Box>
                  </Box>
                </>
              )),
            ]}
          </Box>
        ) : slots.length === 0 &&
          typeof slots === 'object' &&
          Object.keys(slots).length === 0 ? (
          <NoInterviews />
        ) : (
          <>
            {isAboveLarge && (
              <Grid
                container
                mt={{xss: 4.375, md: 2}}
                mx={{xss: 1, md: 0}}
                px={2}
                borderRadius="8px"
                bgcolor="white"
                height={50}
                alignItems="center"
              >
                <Grid item xs={4.8}>
                  <Typography
                    fontWeight={700}
                    fontSize={14}
                    lineHeight={'16px'}
                  >
                    Job Title / Role
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography
                    fontWeight={700}
                    fontSize={14}
                    lineHeight={'16px'}
                  >
                    Interview Type
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography
                    fontWeight={700}
                    fontSize={14}
                    lineHeight={'16px'}
                  >
                    Interview Slot Details
                  </Typography>
                </Grid>
                <Grid item xs={3.2}>
                  <Typography
                    fontWeight={700}
                    fontSize={14}
                    lineHeight={'16px'}
                  >
                    Message from Recruiter
                  </Typography>
                </Grid>
              </Grid>
            )}
            <ContainerComponent
              //key={`${Date.now()}`}
              dataLength={slots?.length}
              hasMore={hasMore}
              next={() => {
                setTimeout(() => {
                  setPageNumber(pageNumber + 1);
                }, 1000);
              }}
              loader={
                <Box
                  sx={{
                    display: 'flex',
                    textAlign: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Loader />
                </Box>
              }
              style={{
                scrollbarWidth: 'none',
              }}
            >
              {slots?.map((slot, index) => {
                return (
                  <Box
                    key={index}
                    bgcolor="white"
                    borderRadius="8px"
                    px={1}
                    mx={{xxs: 2, md: 0}}
                    mt={2}
                    pb={index === slots.length - 1 ? 2 : 0}
                  >
                    {/* {slot.canceledBy === 1 && (
                      <Grid
                        mt={2}
                        mb={{xs: 2, md: 0}}
                        container
                        height={{xs: '44px', md: '36px'}}
                        alignContent="center"
                        justifyContent={{xs: 'start', md: 'center'}}
                        sx={{
                          backgroundColor: 'rgba(237, 187, 74, 0.20)',
                          borderRadius: '8px 8px 0 0',
                        }}
                        pl={1}
                      >
                        <Image
                          src={CautionIcon}
                          height={25}
                          width={25}
                          alt="caution-icon"
                          style={
                            !isAboveLarge
                              ? {
                                  position: 'relative',
                                  top: '2px',
                                }
                              : {}
                          }
                        />
                        <Typography
                          width={{xs: '237px', md: 'auto'}}
                          variant="subtitle3"
                          ml={0.5}
                          lineHeight={'14px'}
                          fontSize={{xs: '10px', md: '12px'}}
                          color={'#333'}
                          alignSelf="center"
                        >
                          The recruiter has canceled this Interview Slot
                          Details. Please choose your slot again.
                        </Typography>
                      </Grid>
                    )} */}
                    <Grid
                      key={slot?.slotId}
                      container
                      mt={slot.canceledBy > 0 ? 0 : 2}
                      borderRadius={slot.canceledBy > 0 ? '0 0 8px 8px' : '8px'}
                      border="none"
                      py={{xxs: 0, md: 2.5}}
                      px={{xxs: 0, md: 1}}
                    >
                      {!isAboveLarge && (
                        <Grid
                          item
                          display="flex"
                          justifyContent="space-between"
                          width="100%"
                          px={1.875}
                          py={2.5}
                        >
                          <InterviewType type={slot?.slotType} isAboveLarge />
                          <InterviewSlot
                            cancelLoading={cancelLoading}
                            inviteStatus={slot.inviteStatus}
                            isAboveLarge={isAboveLarge}
                            handleCancelSlot={handleCancelSlot}
                            booked={slot.slotDate > '2000-01-01'}
                            disabled={slot.remainingSlotsCount <= 0}
                            date={slot.slotDate}
                            timing={slot.slotTime}
                            calendarId={slot.calendarId}
                            slotId={slot.slotId}
                            recruiterId={slot.recruiterId}
                            slotType={slot.slotType}
                          />
                        </Grid>
                      )}
                      <Grid
                        item
                        xs={12}
                        md={4.8}
                        mx={{xxs: 1, md: 0}}
                        pt={{xs: 2, md: 0}}
                        borderTop={{xs: '1px solid #ededf0', md: 'none'}}
                      >
                        <JobTitle
                          title={slot.jobInfo.title}
                          id={slot.jobInfo.id}
                          recruiter={{
                            name: slot.recruiterInfo.name,
                            organization: slot.recruiterInfo.organization,
                            profilePicture: slot.recruiterInfo.imgUrl,
                            confidential: Boolean(slot.jobInfo.confidential),
                          }}
                        />
                      </Grid>
                      {isAboveLarge && (
                        <>
                          <Grid item xxs={6} md={2} mt={{xxs: 2.25, md: 0}}>
                            <InterviewType type={slot?.slotType} isAboveLarge />
                          </Grid>
                          <Grid item xs={2} mt={{xxs: 1, md: 0}}>
                            <InterviewSlot
                              cancelLoading={cancelLoading}
                              inviteStatus={slot.inviteStatus}
                              isAboveLarge={isAboveLarge}
                              handleCancelSlot={handleCancelSlot}
                              booked={slot.slotDate > '2000-01-01'}
                              disabled={slot.remainingSlotsCount <= 0}
                              date={slot.slotDate}
                              timing={slot.slotTime}
                              calendarId={slot.calendarId}
                              slotId={slot.slotId}
                              recruiterId={slot.recruiterId}
                              slotType={slot.slotType}
                            />
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12} md={3.2} pt={{xs: 2.5, md: 0}} pb={0}>
                        <Box
                          bgcolor="rgb(245, 245, 245)"
                          height={isAboveLarge ? 97 : 60}
                          px={1.5}
                          py={1}
                          fontSize={14}
                          color="rgb(33, 37, 41)"
                          overflow="scroll"
                          borderRadius="4px"
                        >
                          {slot?.slotType === 'TELEPHONIC'
                            ? slot?.messageTelephonic
                            : (slot?.address ?? '')}
                        </Box>

                        {!isAboveLarge &&
                          slot.slotType === 'VIDEO' &&
                          dayjs(slot.slotDate).isSame(dayjs(), 'day') && (
                            <Button
                              data-testid="join_room_desktop"
                              onClick={() =>
                                openUrl(
                                  `/interview/meet?inviteId=${decodeHexBase64(
                                    slot.slotId
                                  )}&calleeId=${
                                    slot.recruiterId
                                  }&ref=interviews`,
                                  '_blank'
                                )
                              }
                              variant="outlined"
                              sx={{
                                width: '100%',
                                height: 38,
                                mt: 1.5,
                                borderRadius: 1,
                                fontSize: 14,
                                backgroundColor: `${theme.palette.primary.main} !important`,
                                color: 'white !important',
                                ':hover': {
                                  borderColor: theme.palette.primary.main,
                                  '.searchbar-icon': {
                                    filter: 'brightness(10)',
                                  },
                                },
                                mb: 0.5,
                              }}
                            >
                              Join room
                            </Button>
                          )}
                      </Grid>
                      {index !== slots.length - 1 && !isAboveLarge && (
                        <Grid
                          item
                          xs={12}
                          sx={{borderBottom: '1px solid #F3F7FA', mt: 2}}
                        />
                      )}
                    </Grid>
                  </Box>
                );
              })}
            </ContainerComponent>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Interviews;
